import * as THREE from "three";

// Volumetric Atmospheric Scattering Shader based on Sean O'Neil's Accurate Atmospheric Scattering method.

const vertexShader = `
varying vec3 vViewPosition;
varying vec3 vWorldPosition;
varying vec3 vNormal;

void main() {
    vNormal = normalize(normalMatrix * normal);
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    vec4 viewPosition = viewMatrix * worldPosition;
    vViewPosition = viewPosition.xyz;
    gl_Position = projectionMatrix * viewPosition;
}
`;

const fragmentShader = `
uniform vec3 sunPosition;
uniform vec3 planetCenter;
uniform float planetRadius;
uniform float atmosphereRadius;

// Scattering coefficients
uniform vec3 rayleighScatteringCoefficient;
uniform float mieScatteringCoefficient;
uniform float mieDirectionalG;
uniform vec3 ozoneAbsorptionCoefficient;
uniform float sunIntensity;
// Absolute heights in scene units (see createAtmosphereMaterial).
uniform float rayleighScaleHeight;
uniform float mieScaleHeight;
uniform vec3 ozoneLayer; // start, peak, end
uniform vec2 airglowLayer; // peak height, width (Gaussian)
uniform vec3 airglowRadiance; // emission per unit length at the peak

varying vec3 vWorldPosition;

// At true thickness a grazing ray crosses many scale heights, so it needs
// more samples than the old ~20x-thick shell did.
const int PRIMARY_STEPS = 24;

// Relative optical depth (air mass) towards zenith cosine c from radius /
// scale height X (Schuler 2012), as in earth-lighting.js. Replaces marching
// each sample's sun ray (8 steps each), which made the shell the most
// expensive thing on screen when zoomed in.
float chapman(float X, float c) {
    float s = sqrt(X);
    if (c >= 0.0) return s / (s * c + 1.0);
    float x0 = sqrt(1.0 - c * c) * X;
    return 2.0 * sqrt(x0) * exp(X - x0) - s / (1.0 - s * c);
}

// Ozone column above height h for the triangular profile (peak density 2).
float ozoneColumnAbove(float h) {
    float start = ozoneLayer.x;
    float peak = ozoneLayer.y;
    float end = ozoneLayer.z;
    if (h >= end) return 0.0;
    if (h >= peak) return (end - h) * (end - h) / (end - peak);
    float upper = end - peak;
    if (h >= start) return upper + (peak - start) - (h - start) * (h - start) / (peak - start);
    return end - start;
}

vec2 raySphereIntersect(vec3 r0, vec3 rd, vec3 s0, float sr) {
    float a = dot(rd, rd);
    vec3 s0_r0 = r0 - s0;
    float b = 2.0 * dot(rd, s0_r0);
    float c = dot(s0_r0, s0_r0) - (sr * sr);
    float d = (b*b) - 4.0*a*c;
    if (d < 0.0) return vec2(1e5, -1e5);
    return vec2((-b - sqrt(d))/(2.0*a), (-b + sqrt(d))/(2.0*a));
}

// Ozone layer is modeled as a triangular density function
// Airglow: light emitted by the upper air itself (mostly oxygen's green line)
// from a thin layer ~90 km up, day and night. Looking down it's far too faint
// to see; edge-on at the limb the path through it is ~50x longer, so it
// outlines the night side as a thin green line. Marched only across the
// layer: the near crossing, and the far one if the ray passes above the
// layer's base and misses the planet. Only counted where the layer is in
// darkness: photos of the day side never show it against sunlit air.
vec3 airglow(vec3 ro, vec3 rd, float tMax, vec3 sunDir) {
    float bottom = planetRadius + airglowLayer.x - 2.5 * airglowLayer.y;
    float top = min(atmosphereRadius, planetRadius + airglowLayer.x + 2.5 * airglowLayer.y);
    vec2 outer = raySphereIntersect(ro, rd, planetCenter, top);
    if (outer.y < 0.0) return vec3(0.0);
    vec2 inner = raySphereIntersect(ro, rd, planetCenter, bottom);
    float a0 = max(outer.x, 0.0);
    float a1 = min(inner.x < inner.y ? inner.x : outer.y, tMax);
    float b0 = inner.x < inner.y ? max(inner.y, 0.0) : 1e9;
    float b1 = min(outer.y, tMax);
    float total = 0.0;
    for (int k = 0; k < 2; k++) {
        float s0 = k == 0 ? a0 : b0;
        float s1 = k == 0 ? a1 : b1;
        if (s1 <= s0) continue;
        float ds = (s1 - s0) / 12.0;
        for (int i = 0; i < 12; i++) {
            vec3 p = ro + rd * (s0 + (float(i) + 0.5) * ds) - planetCenter;
            float r = length(p);
            float x = (r - planetRadius - airglowLayer.x) / airglowLayer.y;
            float dark = smoothstep(0.1, -0.15, dot(p / r, sunDir));
            total += exp(-x * x) * dark * ds;
        }
    }
    return airglowRadiance * total;
}

float getOzoneDensity(float h, float pRadius) {
    float start = ozoneLayer.x;
    float peak = ozoneLayer.y;
    float end = ozoneLayer.z;
    
    float density = 0.0;
    if (h > start && h < end) {
        if (h < peak) {
            density = (h - start) / (peak - start);
        } else {
            density = (end - h) / (end - peak);
        }
    }
    return max(0.0, density) * 2.0; // Base density multiplier
}

void main() {
    vec3 rayOrigin = cameraPosition;
    vec3 rayDirection = normalize(vWorldPosition - cameraPosition);

    vec2 pAtmosphere = raySphereIntersect(rayOrigin, rayDirection, planetCenter, atmosphereRadius);
    vec2 pPlanet = raySphereIntersect(rayOrigin, rayDirection, planetCenter, planetRadius);

    if (pAtmosphere.y < 0.0) discard;

    float tMin = max(0.0, pAtmosphere.x);
    float tMax = pAtmosphere.y;
    
    if (pPlanet.x > 0.0) {
        tMax = min(tMax, pPlanet.x);
    }

    if (tMin >= tMax) discard;

    float segmentLength = (tMax - tMin) / float(PRIMARY_STEPS);

    float opticalDepthRayleigh = 0.0;
    float opticalDepthMie = 0.0;
    float opticalDepthOzone = 0.0;
    // Air along the view ray that the sun actually reaches (not in Earth's shadow).
    float opticalDepthLitRayleigh = 0.0;

    vec3 totalRayleigh = vec3(0.0);
    vec3 totalMie = vec3(0.0);

    vec3 sunDir = normalize(sunPosition - planetCenter);

    // Per-pixel jitter of the sample positions turns step banding into fine noise.
    float jitter = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
    float currentT = tMin + segmentLength * jitter;


    for (int i = 0; i < PRIMARY_STEPS; i++) {
        vec3 currentPos = rayOrigin + rayDirection * currentT;
        float h = max(0.0, length(currentPos - planetCenter) - planetRadius);

        // Density functions
        float densityRayleigh = exp(-h / rayleighScaleHeight) * segmentLength;
        float densityMie = exp(-h / mieScaleHeight) * segmentLength;
        float densityOzone = getOzoneDensity(h, planetRadius) * segmentLength;

        opticalDepthRayleigh += densityRayleigh;
        opticalDepthMie += densityMie;
        opticalDepthOzone += densityOzone;

        vec2 pdSun = raySphereIntersect(currentPos, sunDir, planetCenter, planetRadius);

        // Shadow check
        if (pdSun.x > 0.0) {
            currentT += segmentLength;
            continue;
        }

        opticalDepthLitRayleigh += densityRayleigh;

        // Optical depth along the sun ray to space: the vertical column
        // above this point times the air mass towards the sun.
        float r = h + planetRadius;
        float mu = dot(currentPos - planetCenter, sunDir) / r;
        float airmassRayleigh = min(chapman(r / rayleighScaleHeight, mu), 1e4);
        float opticalDepthLightRayleigh = exp(-h / rayleighScaleHeight) * rayleighScaleHeight * airmassRayleigh;
        float opticalDepthLightMie = exp(-h / mieScaleHeight) * mieScaleHeight * min(chapman(r / mieScaleHeight, mu), 1e4);
        float opticalDepthLightOzone = ozoneColumnAbove(h) * min(airmassRayleigh, 40.0);

        // Bruneton combined extinction: Rayleigh + Mie + Ozone absorption
        vec3 extinction = (opticalDepthRayleigh + opticalDepthLightRayleigh) * rayleighScatteringCoefficient +
                          (opticalDepthMie + opticalDepthLightMie) * vec3(mieScatteringCoefficient) +
                          (opticalDepthOzone + opticalDepthLightOzone) * ozoneAbsorptionCoefficient;
                          
        vec3 attenuation = exp(-extinction);

        totalRayleigh += densityRayleigh * attenuation;
        totalMie += densityMie * attenuation;

        currentT += segmentLength;
    }

    float cosTheta = dot(rayDirection, sunDir);
    float cosThetaSq = cosTheta * cosTheta;

    // Rayleigh Phase
    float phaseRayleigh = 3.0 / (16.0 * 3.14159) * (1.0 + cosThetaSq);

    // Cornette-Shanks Phase (often replacing simple Henyey-Greenstein in Bruneton models for better Mie accuracy)
    float g = mieDirectionalG;
    float gSq = g * g;
    float phaseMie = 3.0 / (8.0 * 3.14159) * ((1.0 - gSq) * (1.0 + cosThetaSq)) / 
                     ((2.0 + gSq) * pow(1.0 + gSq - 2.0 * g * cosTheta, 1.5));

    // Approximate multiple scattering (inspired by Elek): softens the terminator.
    // Only sunlit air contributes, so air in Earth's shadow stays dark; using
    // all air along the ray lit the night-side limb.
    // Weighted like single scattering with an isotropic phase (1/4pi) at ~30%
    // strength; a bare 0.2 dominated once coefficients became Earth-like.
    vec3 multiScatteringAmbient = (opticalDepthLitRayleigh * rayleighScatteringCoefficient) * (0.3 / (4.0 * 3.14159));

    // Final composition
    vec3 color = (totalRayleigh * rayleighScatteringCoefficient * phaseRayleigh) + 
                 (totalMie * vec3(mieScatteringCoefficient) * phaseMie) + 
                 multiScatteringAmbient;

    // Linear radiance for the HDR pipeline (tone mapped once, at output).
    // sunIntensity is the same solar irradiance that lights the surface.
    color *= sunIntensity;
    color += airglow(rayOrigin, rayDirection, tMax, sunDir);
    gl_FragColor = vec4(color, 1.0);
}
`;

// Earth at true proportions: real sea-level coefficients (per metre), scale
// heights, ozone layer and a 100 km top, converted to scene units by
// the planet radius. Optical depths therefore match Earth's (vertical
// Rayleigh ~ (0.04, 0.10, 0.18), Mie ~0.03, ozone ~0.03 in green), and at
// globe zoom the atmosphere is a thin line at the limb, as seen from orbit.
const EARTH_RADIUS_M = 6371000;
const RAYLEIGH_SEA_LEVEL = [5.5e-6, 13.0e-6, 22.4e-6];
const RAYLEIGH_SCALE_HEIGHT_M = 8000;
const MIE_SEA_LEVEL = 21e-6;
const MIE_SCALE_HEIGHT_M = 1200;
const OZONE_LAYER_M = [10000, 25000, 50000]; // start, peak, end
const OZONE_VERTICAL_DEPTH = [0.0098, 0.028, 0.0013];
const ATMOSPHERE_TOP_M = 100000;
// Oxygen green-line airglow layer: peak and width (inside the 100 km top),
// and its colour (557.7 nm, yellow-green) at a brightness picked so the
// limb line reads at night exposure; looking down it stays negligible.
const AIRGLOW_PEAK_M = 91000;
const AIRGLOW_WIDTH_M = 3500;
const AIRGLOW_COLOUR = [0.45, 1.0, 0.25];
const AIRGLOW_BRIGHTNESS = 0.002; // per scene unit of path at the peak

export function createAtmosphereMaterial(options = {}) {
    const planetRadius = options.planetRadius ?? 100.0;
    const unitsPerMetre = planetRadius / EARTH_RADIUS_M;
    const metresPerUnit = 1 / unitsPerMetre;
    const ozoneLayer = new THREE.Vector3(...OZONE_LAYER_M).multiplyScalar(unitsPerMetre);
    // The triangular ozone profile (peak density 2) integrates to end - start.
    const ozoneColumn = ozoneLayer.z - ozoneLayer.x;
    const {
        sunPosition = new THREE.Vector3(0, 0, 0),
        planetCenter = new THREE.Vector3(0, 0, 0),
        atmosphereRadius = planetRadius + ATMOSPHERE_TOP_M * unitsPerMetre,
        rayleighScatteringCoefficient = new THREE.Vector3(...RAYLEIGH_SEA_LEVEL).multiplyScalar(metresPerUnit),
        mieScatteringCoefficient = MIE_SEA_LEVEL * metresPerUnit,
        ozoneAbsorptionCoefficient = new THREE.Vector3(...OZONE_VERTICAL_DEPTH).divideScalar(ozoneColumn),
        mieDirectionalG = 0.758,
        // Solar irradiance, in the same units as the scene's sun light.
        sunIntensity = 2.0,
    } = options;

    return new THREE.ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
            sunPosition: { value: sunPosition },
            planetCenter: { value: planetCenter },
            planetRadius: { value: planetRadius },
            atmosphereRadius: { value: atmosphereRadius },
            rayleighScatteringCoefficient: { value: rayleighScatteringCoefficient },
            mieScatteringCoefficient: { value: mieScatteringCoefficient },
            ozoneAbsorptionCoefficient: { value: ozoneAbsorptionCoefficient },
            mieDirectionalG: { value: mieDirectionalG },
            sunIntensity: { value: sunIntensity },
            rayleighScaleHeight: { value: RAYLEIGH_SCALE_HEIGHT_M * unitsPerMetre },
            mieScaleHeight: { value: MIE_SCALE_HEIGHT_M * unitsPerMetre },
            ozoneLayer: { value: ozoneLayer },
            airglowLayer: { value: new THREE.Vector2(AIRGLOW_PEAK_M, AIRGLOW_WIDTH_M).multiplyScalar(unitsPerMetre) },
            airglowRadiance: { value: new THREE.Vector3(...AIRGLOW_COLOUR).multiplyScalar(AIRGLOW_BRIGHTNESS) },
        },
        transparent: true,
        side: THREE.FrontSide,
        depthWrite: false,
        // Adds the inscattered light. Dimming of what's behind (aerial
        // perspective, per colour) is done by the surface and cloud shaders
        // (earth-lighting.js), which blending's single alpha can't do.
        blending: THREE.AdditiveBlending,
    });
}
